import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDocument, useCollection, useFirestoreMutations } from '../../hooks/useFirestore'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { storage } from '../../lib/firebase'
import { useDropzone } from 'react-dropzone'
import ImageUploader from './ImageUploader'
import HarptosDatePicker from './HarptosDatePicker'
import './AdminEditor.css'

const EMPTY_HANDOUT = {
  title: '',
  description: '',
  fileUrl: '',
  thumbnailUrl: '',
  receivedDate: null, // Harptos date when handout was received in-game
  hidden: false,
  sortOrder: 0,
  characterIds: [] // Characters linked to this handout (sent by / received by)
}

export default function HandoutEditor() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isNew = id === 'new'

  const { data: existingHandout, loading } = useDocument('handouts', isNew ? null : id)
  const { data: characters } = useCollection('characters', { orderByField: 'name' })
  const { add, update, remove } = useFirestoreMutations('handouts')

  const [handout, setHandout] = useState(EMPTY_HANDOUT)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (existingHandout) {
      setHandout({ ...EMPTY_HANDOUT, ...existingHandout })
    }
  }, [existingHandout])

  const handleChange = (field, value) => {
    setHandout(prev => ({ ...prev, [field]: value }))
  }

  const handleCharacterToggle = (charId) => {
    setHandout(prev => {
      const currentIds = prev.characterIds || []
      if (currentIds.includes(charId)) {
        return { ...prev, characterIds: currentIds.filter(id => id !== charId) }
      } else {
        return { ...prev, characterIds: [...currentIds, charId] }
      }
    })
  }

  async function handleFileUpload(acceptedFiles) {
    const file = acceptedFiles[0]
    if (!file) return

    setUploading(true)
    setError(null)

    try {
      const timestamp = Date.now()
      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_')
      const path = `handouts/${timestamp}-${safeName}`
      const storageRef = ref(storage, path)

      await uploadBytes(storageRef, file)
      const url = await getDownloadURL(storageRef)

      setHandout(prev => ({
        ...prev,
        fileUrl: url,
        title: prev.title || file.name.replace(/\.[^/.]+$/, '')
      }))
    } catch (err) {
      console.error('Error uploading file:', err)
      setError('Failed to upload file')
    } finally {
      setUploading(false)
    }
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileUpload,
    multiple: false,
    disabled: uploading
  })

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!handout.title.trim()) {
      setError('Title is required')
      return
    }

    if (!handout.fileUrl) {
      setError('Please upload a file')
      return
    }

    setSaving(true)
    setError(null)

    try {
      if (isNew) {
        await add(handout)
      } else {
        await update(id, handout)
      }
      navigate('/admin/handouts')
    } catch (err) {
      setError(err.message)
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${handout.title}"? This cannot be undone.`)) {
      return
    }

    setSaving(true)
    try {
      await remove(id)
      navigate('/admin/handouts')
    } catch (err) {
      setError(err.message)
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="container">
        <div className="loading-spinner" />
      </div>
    )
  }

  return (
    <div className="container">
      <div className="editor-header">
        <h1>{isNew ? 'New Handout' : 'Edit Handout'}</h1>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => navigate('/admin/handouts')}
        >
          ← Back
        </button>
      </div>

      <form onSubmit={handleSubmit} className="editor-form">
        {error && <div className="error-banner">{error}</div>}

        <div className="form-group">
          <label>File *</label>
          <div
            {...getRootProps()}
            className={`dropzone ${isDragActive ? 'active' : ''} ${uploading ? 'uploading' : ''}`}
          >
            <input {...getInputProps()} />
            {uploading ? (
              <p>Uploading...</p>
            ) : handout.fileUrl ? (
              <div>
                <p>File uploaded</p>
                <a
                  href={handout.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  className="text-secondary"
                >
                  View file →
                </a>
                <p className="help-text" style={{ marginTop: 'var(--space-sm)' }}>Drop a new file to replace</p>
              </div>
            ) : (
              <p>Drop a file here, or click to select</p>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="title">Title *</label>
          <input
            id="title"
            type="text"
            value={handout.title}
            onChange={(e) => handleChange('title', e.target.value)}
            placeholder="Handout title"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            value={handout.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Brief description of this handout..."
            rows={3}
          />
        </div>

        <div className="form-group">
          <HarptosDatePicker
            label="Date Received (in-game)"
            value={handout.receivedDate}
            onChange={(date) => handleChange('receivedDate', date)}
          />
          <p className="help-text">When this handout was received during the campaign</p>
        </div>

        <div className="form-group">
          <label>Thumbnail (optional)</label>
          <ImageUploader
            value={handout.thumbnailUrl}
            onChange={(url) => handleChange('thumbnailUrl', url)}
            folder="handouts/thumbnails"
          />
          <p className="help-text">Preview image shown in the handouts list</p>
        </div>

        <div className="form-group">
          <label>Linked Characters</label>
          <p className="help-text" style={{ marginTop: 0, marginBottom: 'var(--space-sm)' }}>
            Characters who sent, received, or are associated with this handout
          </p>
          <div className="character-checkboxes">
            {characters.map(char => (
              <label key={char.id} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={(handout.characterIds || []).includes(char.id)}
                  onChange={() => handleCharacterToggle(char.id)}
                />
                <span>
                  {char.name}
                  <span className="char-type">({char.type?.toUpperCase()})</span>
                </span>
              </label>
            ))}
            {characters.length === 0 && (
              <p className="text-secondary">No characters available</p>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="sortOrder">Sort Order</label>
            <input
              id="sortOrder"
              type="number"
              value={handout.sortOrder}
              onChange={(e) => handleChange('sortOrder', parseInt(e.target.value) || 0)}
              style={{ maxWidth: '100px' }}
            />
            <p className="help-text">Lower numbers appear first</p>
          </div>

          <div className="form-group form-group-inline">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={handout.hidden === true}
                onChange={(e) => handleChange('hidden', e.target.checked)}
              />
              <span>Hidden (Spoiler)</span>
            </label>
            <span className="help-text">Only visible in admin</span>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
          >
            {saving ? 'Saving...' : (isNew ? 'Create Handout' : 'Save Changes')}
          </button>

          {!isNew && (
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleDelete}
              disabled={saving}
            >
              Delete Handout
            </button>
          )}
        </div>
      </form>
    </div>
  )
}
